import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import homeStore from './homeStore';
import omaveroStore from './omaveroStore';
import ostolaskuStore from './ostolaskuStore';
import receiptStore from './receiptStore';
import routing from './routingStore';
import Storage from '../Storage';
import Http from '../Http';
import consts from '../consts';
import tulosStore from './tulosStore';
import viewStore from './viewStore';
import tasevastaavaaStore from './tasevastaavaaStore';
import tasevastattavaaStore from './tasevastattavaaStore';


const ERROR_INVALID = 1;
const ERROR_PERMISSION = 2;
const ERROR_GENERIC = 3;
const ERROR_SHORT_PASSWORD = 4;

class AuthStore {
  @observable authenticated = false;
  @observable error = false;
  @observable loading = false;
  @observable loginDialogLoading = false;
  @observable errorCompanies = false;
  @observable loadingCompanies = false;
  @observable username = '';
  @observable password = '';
  @observable oldpassword = '';
  @observable newpassword = '';
  @observable newpasswordc = '';
  @observable companies;
  @observable currentCompany;

  // If false asks for password with [LoginDialog] or [LoginToCompany].
  // [LoginToCompany] is used at startup if there is not companies without password else we show the first company with no password.
  @observable companyAuthenticated = false;
  // Temporary company that the user tries to log in to. Need to give [QUID] and [NAME] because [LoginDialog] is part of the UI even if its not shown.
  // NOT same as [currentCompany], [tempCompany] will be [currentCompany] when user succeeds with login.
  @observable tempCompany = {
    NAME: "empty",
    QUID: "empty",
  };
  @observable companyPassword = '';



  jwt;

  @computed
  get companyToken() {
    if (!this.currentCompany) return undefined;

    return this.currentCompany.contractid + '.' + this.currentCompany.COMPANYID;
  }

  constructor() {
    makeObservable(this);
    this.restore();
  }

  hasModule(moduleId) {
    return this.currentCompany && this.currentCompany.modules && this.currentCompany.modules.includes(moduleId);
  }

  @action.bound
  clear() {
    this.authenticated = false;
    this.companyAuthenticated = false;
    this.jwt = null;
    this.companies = null;
    this.currentCompany = null;
    this.tempCompany = {
      NAME: "empty",
      QUID: "empty",
    };
    this.oldpassword = '';
    this.newpassword = '';
    this.newpasswordc = '';
  }

  @action.bound
  async fetchCompanies() {
    this.loadingCompanies = true;
    this.errorCompanies = false;

    try {
      const companies = await Http.request('companies');

      runInAction(() => {
        this.companies = companies;

        // update currentCompany
        if (this.currentCompany) {
          let currentCompany = this.companies.find(company =>
            company.COMPANYID === this.currentCompany.COMPANYID && company.contractid === this.currentCompany.contractid
          );

          // selected company doesn't exist anymore
          if (!currentCompany) {
            this.currentCompany.modules = [];
          } else {
            this.currentCompany = currentCompany;
          }

          Storage.set('currentCompany', this.currentCompany);

        }
      });

      Storage.set('companies', this.companies);
    } catch (error) {
      runInAction(() => {
        this.errorCompanies = true;
      });
    }

    runInAction(() => {
      this.loadingCompanies = false;
    });

    return this.companies;
  }
  @action.bound
  async login() {

    if (this.loading || !this.username || !this.password) {
      return;
    }

    this.error = false;
    this.loading = true;

    try {
      const response = await Http.request('login', {
        method: 'post',
        json: {
          username: this.username,
          password: this.password,
        }
      });

      this.setJWT(response.token);

      // When loggin in fetchCompanies
      // TODO: handle fail
      await this.fetchCompanies();

      let loop = true;
      let i = 0;
      // Loop until find company without password.
      while (loop) {
        // Check if password.
        if (this.companies[i].PASSWORD === 0) {
          this.selectCompany(this.companies[i]);
          this.companyAuthenticated = true;
          Storage.set('companyAuthenticated', this.companyAuthenticated);
          loop = false;
        }
        else {
          // Check if last index. 
          // If last index and we haven't found company without password we ask password from the first company in the array.
          if (i === this.companies.length - 1) {
            loop = false;
            this.setTempCompany(this.companies[0]);
          }
          else {
            i++;
          }

        }

      }

      runInAction(() => {
        // Set authenticated to true
        this.authenticated = true;
        // Clear username and password
        this.username = '';
        this.password = '';
      });
    } catch (error) {
      runInAction(() => {
        // Show error
        this.error = error.status === 401 ? ERROR_INVALID :
          error.status === 403 ? ERROR_PERMISSION :
            ERROR_GENERIC;
      });
      // If fail login we clear everything with [logout()]
      this.logout();
    }

    runInAction(() => {
      // Stop loading
      this.loading = false;
    });
  }

  @action.bound
  async changePassword() {

    if (this.loading) {

      return ;
    }

    if (!this.oldpassword  || !this.newpassword || !this.newpasswordc) {
      viewStore.showSnackbar('Tarkista että olet täyttänyt kaikki kentät');
      return ;
    }

    if (this.newpassword !== this.newpasswordc) {
      viewStore.showSnackbar('Tarkista onko uusi salasana ja sen kopio sama');
      return ;
    }

    this.error = false;
    this.loading = true;

    try {
      const response = await Http.request('changepassword', {
        method: 'post',
        json: {
          oldpassword: this.oldpassword,
          newpassword: this.newpassword,
          newpasswordc: this.newpasswordc
        }
      });
      if(response && response.successfull && response.successfull === 'true'){
        this.logout();
      }
      else if(response && response.error && response.error === 'short_password'){
        runInAction(() => {
          // Show error
          this.error =  ERROR_SHORT_PASSWORD;
        });
      }
   
    } catch (error) {
      runInAction(() => {
        // Show error
        this.error = error.status === 401 ? ERROR_INVALID :
          error.status === 403 ? ERROR_PERMISSION :
            ERROR_GENERIC;
      });
    }

    runInAction(() => {
      // Stop loading
      this.loading = false;
    });
  }

  clearStores() {
    receiptStore.clear();
    ostolaskuStore.clear();
    homeStore.clear();
    omaveroStore.clear();
    tulosStore.clear();
    tasevastaavaaStore.clear();
    tasevastattavaaStore.clear();
  }

  @action
  logout() {
    this.clear();
    this.clearStores();
    Storage.clear();
    Http.clear();
  }

  @action
  async refreshExpiringToken() {
    await Http.refreshRequest();
  }

  @action.bound
  async restore() {

    this.jwt = Storage.get('jwt');

    if (!this.jwt) {
      return;
    }

    Http.setJWT(this.jwt);

    this.authenticated = true;

    this.currentCompany = Storage.get('currentCompany');
    // After refresh get tempCompany.
    this.tempCompany = Storage.get('tempCompany');
    this.companyAuthenticated = Storage.get('companyAuthenticated');

    const companies = Storage.get('companies');

    if (companies) {
      this.companies = companies;
    }

    this.fetchCompanies();
  }

  @action.bound
  selectCompany(company) {
    this.currentCompany = company;
    Storage.set('currentCompany', company);
    Storage.set('tempCompany', this.tempCompany);

    ostolaskuStore.reset();
    homeStore.reset();
    omaveroStore.reset();
    tulosStore.reset();
    tasevastaavaaStore.reset();
    tasevastattavaaStore.reset();
    routing.push('/tulos');
    homeStore.fetch();
    tulosStore.refresh();
  }

  @action.bound
  setJWT(token) {
    this.jwt = token;
    Http.setJWT(token);

    Storage.set('jwt', token);
  }

  @action.bound
  setPassword(value) {
    this.password = value;
  }

  @action.bound
  setUsername(value) {
    this.username = value;
  }

  @action.bound
  setOldPassword(value) {
    this.oldpassword = value;
  }

  @action.bound
  setNewPassword(value) {
    this.newpassword = value;
  }

  @action.bound
  setNewPasswordC(value) {
    this.newpasswordc = value;
  }

  getTositelaji(id) {
    return this.currentCompany.tositelajit.find(tositelaji => tositelaji.TOSITELAJIID === id);
  }

  getAttachmentURL(attachment, type) {
    return consts.API + 'contracts/' + this.currentCompany.contractid + '/companies/' + this.currentCompany.COMPANYID + '/' + type + '/liitteet/' + attachment.GUID + '?token=' + this.jwt;
  }

  // Logs user into a company that has password protection.
  @action.bound
  async loginCompany() {

    this.error = false;
    this.loginDialogLoading = true;

    try {
      const response = await Http.request('companies', {
        method: 'post',
        json: {
          name: this.tempCompany.NAME,
          guid: this.tempCompany.GUID,
          password: this.companyPassword,
        }
      });
      if (response.password === "correct") {
        viewStore.closeLoginDialog();
        this.selectCompany(this.tempCompany);
        runInAction(() => {
          this.companyAuthenticated = true;

        })

        Storage.set('companyAuthenticated', this.companyAuthenticated);

      } else if (response.password === "invalid") {
        // INVALID password
        this.error = 1;
      }
    } catch (error) {
      runInAction(() => {
        // Show error
        this.error = error.status === 401 ? ERROR_INVALID :
          error.status === 403 ? ERROR_PERMISSION :
            ERROR_GENERIC;
      });
    }

    runInAction(() => {
      // Stop loading
      this.loginDialogLoading = false;
    });
  }
  // Used to set the [tempCompany] that user tries to log in to.
  @action.bound
  setTempCompany(company) {
    this.tempCompany = company;
    Storage.set('tempCompany', this.tempCompany);
  }
  // Used to set the [password] of the [tempCompany] that user tries to log in to.
  @action.bound
  setTempCompanyPassword(companyPassword) {
    this.companyPassword = companyPassword;
  }
}

export default new AuthStore();
